import { PageHero } from "@/components/site/PageHero";
import { Reveal, Eyebrow } from "@/components/site/motion";
import { PhotoFrame } from "@/components/site/Placeholders";
import { Icon } from "@/components/site/Icon";
import { CtaBand } from "@/components/site/CtaBand";
import { VALUES, SLOGAN } from "@/data/site";

const SubNav = () => (
  <div className="mt-10 flex flex-wrap gap-3">
    {[["storia", "La nostra storia"], ["missione", "Missione e valori"], ["modello", "Il nostro modello organizzativo"]].map(([id, l]) => (
      <a key={id} href={`#${id}`} data-testid={`about-subnav-${id}`} className="rounded-full border border-navy/15 bg-white px-4 py-2 text-sm font-semibold text-navy transition-colors hover:border-gold hover:bg-gold/10">
        {l}
      </a>
    ))}
  </div>
);

const Storia = () => (
  <section id="storia" data-testid="about-history" className="scroll-mt-24 bg-white py-20 md:py-28">
    <div className="mx-auto grid max-w-7xl gap-14 px-5 sm:px-8 lg:grid-cols-2 lg:gap-20">
      <div>
        <Reveal><Eyebrow>La nostra storia</Eyebrow></Reveal>
        <Reveal delay={0.1}><h2 className="mt-6 text-3xl font-semibold leading-tight text-navy md:text-[2.6rem]">Nasciamo per connettere.</h2></Reveal>
        <Reveal delay={0.2}>
          <p className="mt-6 text-base leading-relaxed text-slate2 md:text-lg">
            Nexus Italia nasce nell'agosto del 2025 con un obiettivo chiaro: creare connessioni che fanno la differenza.
          </p>
          <p className="mt-4 text-base leading-relaxed text-slate2">
            Unisce enti del soccorso, professionisti sanitari e strutture pubbliche e private in una rete coordinata. Chi ha bisogno di un servizio, che sia un cittadino, una struttura o un'azienda, può contare su un unico punto di riferimento: la Centrale Operativa nazionale.
          </p>
          <p className="mt-4 text-base leading-relaxed text-slate2">
            Accanto ai servizi sanitari offriamo consulenza specializzata per il Terzo Settore, accompagnando associazioni, cooperative e organizzazioni sanitarie nel loro percorso di crescita, sviluppo e strutturazione.
          </p>
          <p className="mt-8 border-l-2 border-gold pl-5 text-lg font-semibold leading-snug text-navy">
            Nexus Italia significa esserci. Con professionalità, competenza e un impegno reale verso le persone.
          </p>
        </Reveal>
      </div>
      <Reveal delay={0.1}>
        <PhotoFrame src="/media/logo-completo.jpg" fit="contain" label="Nexus Italia Group – Esserci. Insieme. Sempre e ovunque." className="min-h-[300px] rounded-[28px] border border-navy/10 md:min-h-[480px]" testId="about-history-photo" />
      </Reveal>
    </div>
  </section>
);

const Missione = () => (
  <section id="missione" data-testid="about-mission" className="scroll-mt-24 bg-mist py-20 md:py-28">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <div className="grid gap-10 lg:grid-cols-[1fr_1.2fr] lg:items-end">
        <div>
          <Reveal><Eyebrow>Missione e valori</Eyebrow></Reveal>
          <Reveal delay={0.1}><h2 className="mt-6 text-3xl font-semibold leading-tight text-navy md:text-[2.6rem]">La nostra missione</h2></Reveal>
        </div>
        <Reveal delay={0.2}>
          <p className="text-base leading-relaxed text-slate2 md:text-lg">
            Organizzare, coordinare e rendere accessibili servizi sanitari e sociosanitari, mettendo in relazione chi ha un bisogno con i professionisti in grado di rispondere, con serietà, chiarezza e rispetto.
          </p>
        </Reveal>
      </div>
      <Reveal className="mt-14 border-y border-navy/10 py-8 text-center">
        <p className="text-lg font-semibold tracking-[0.18em] text-navy md:text-2xl">{SLOGAN}</p>
      </Reveal>
      <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {VALUES.map((v, i) => (
          <Reveal key={v.title} delay={i * 0.1} className="group rounded-[22px] border border-navy/10 bg-white p-8 transition-[transform,box-shadow] duration-500 hover:-translate-y-1 hover:shadow-[0_20px_50px_rgba(24,49,79,0.10)]">
            <span className="flex h-14 w-14 items-center justify-center rounded-full border border-gold/50 transition-colors duration-500 group-hover:bg-navy">
              <Icon name={v.icon} className="h-6 w-6 text-navy transition-colors duration-500 group-hover:text-gold" />
            </span>
            <h3 className="mt-8 text-xl font-semibold text-navy" data-testid={`value-${v.title}`}>{v.title}</h3>
            <p className="mt-3 text-sm leading-relaxed text-slate2">{v.text}</p>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const FLOW = [
  { t: "Chi ha un bisogno", d: "Cittadini, strutture sanitarie, aziende e organizzazioni inviano la richiesta." },
  { t: "Centrale Operativa Nexus", d: "Riceve, analizza, pianifica e coordina. È il punto di riferimento unico.", core: true },
  { t: "Rete di partner operativi", d: "Partner qualificati svolgono il servizio sul territorio, coordinati da Nexus." },
];

const Modello = () => (
  <section id="modello" data-testid="about-model" className="scroll-mt-24 bg-white py-20 md:py-28">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <Reveal><Eyebrow>Il nostro modello organizzativo</Eyebrow></Reveal>
      <Reveal delay={0.1}><h2 className="mt-6 max-w-3xl text-3xl font-semibold leading-tight text-navy md:text-[2.6rem]">Non una semplice società di trasporto: un'organizzazione che coordina.</h2></Reveal>
      <Reveal delay={0.2}>
        <p className="mt-6 max-w-3xl text-base leading-relaxed text-slate2 md:text-lg">
          Il nostro modello unisce competenze, tecnologia, coordinamento e operatori qualificati. La Centrale Operativa è il centro del sistema: raccoglie le richieste e le collega al partner più adatto, seguendo ogni servizio fino alla conclusione.
        </p>
      </Reveal>
      <div className="relative mt-16 grid gap-5 lg:grid-cols-3">
        <div className="pointer-events-none absolute left-1/2 top-0 h-full w-px -translate-x-1/2 bg-gold/50 lg:left-0 lg:top-1/2 lg:h-px lg:w-full lg:translate-x-0" />
        {FLOW.map((f, i) => (
          <Reveal key={f.t} delay={i * 0.15} className={`relative rounded-[22px] border p-8 md:p-10 ${f.core ? "border-navy bg-navy text-white" : "border-navy/10 bg-white"}`}>
            <span className={`text-xs font-semibold tracking-[0.2em] ${f.core ? "text-gold" : "text-gold-ink"}`}>0{i + 1}</span>
            <h3 className={`mt-4 text-xl font-semibold ${f.core ? "text-white" : "text-navy"}`}>{f.t}</h3>
            <p className={`mt-3 text-sm leading-relaxed ${f.core ? "text-white/80" : "text-slate2"}`}>{f.d}</p>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export default function ChiSiamo() {
  return (
    <div data-testid="about-page">
      <PageHero
        eyebrow="Chi siamo"
        lines={["Organizziamo.", "Coordiniamo.", "Ci siamo."]}
        intro="Nexus Italia Group organizza, gestisce e coordina servizi sanitari e sociosanitari, mettendo in comunicazione persone, strutture e organizzazioni con una rete qualificata di partner operativi."
      >
        <SubNav />
      </PageHero>
      <Storia />
      <Missione />
      <Modello />
      <CtaBand />
    </div>
  );
}
